<template>
  <b-modal
    :visible="show"
    centered
    size="lg"
    :title="isEditing ? 'Edit Role' : 'Create Role'"
    hide-footer
    no-close-on-backdrop
    @change="onVisibilityChange"
    @hidden="onHidden"
  >
    <div v-if="isLoading" class="text-center py-5">
      <b-spinner variant="primary" />
    </div>

    <b-form v-else @submit.prevent="save">
      <b-alert :show="!!systemRole" variant="info" class="py-2">
        This is a system role and cannot be renamed or deleted. Its permissions
        are managed by Tradie Pass.
      </b-alert>

      <b-form-group label="Role name">
        <b-form-input
          v-model="form.name"
          placeholder="e.g. Site Manager"
          :disabled="systemRole"
          required
        />
      </b-form-group>

      <b-form-group label="Description (optional)">
        <b-form-textarea
          v-model="form.description"
          placeholder="What can this role do?"
          rows="2"
          :disabled="systemRole"
        />
      </b-form-group>

      <div class="border rounded p-3 mb-3 scope-section">
        <b-form-checkbox
          v-model="form.is_project_scoped"
          :disabled="systemRole"
          switch
          class="mb-1 scope-switch"
        >
          Limit this role to specific assigned projects
        </b-form-checkbox>
        <small
          class="text-muted d-block"
          :class="form.is_project_scoped && !systemRole ? 'mb-3' : 'mb-0'"
        >
          When on, users with this role can only access the projects picked
          below. When off, they act across all company projects.
        </small>

        <template v-if="form.is_project_scoped && !systemRole">
          <hr class="mt-2 mb-3" />
          <project-access-selector v-model="selectedProjectIds" />
        </template>
      </div>

      <hr />

      <div class="d-flex justify-content-between align-items-center mb-2">
        <h6 class="mb-0 font-weight-bold">Permissions</h6>
        <div
          v-if="!isAdminRole"
          class="d-flex align-items-center"
          style="gap: 12px"
        >
          <small class="text-muted">{{ selectedIds.length }} selected</small>
          <b-button
            variant="link"
            size="sm"
            class="p-0"
            :disabled="systemRole || allPermissionsSelected"
            @click="selectAllPermissions"
          >
            Select all
          </b-button>
          <b-button
            variant="link"
            size="sm"
            class="p-0"
            :disabled="systemRole || selectedIds.length === 0"
            @click="clearAllPermissions"
          >
            Clear all
          </b-button>
        </div>
      </div>

      <b-alert v-if="isAdminRole" show variant="secondary" class="py-2">
        The Admin role always grants full access to every feature. Its
        permissions cannot be restricted - assign a custom role to limit what
        someone can do.
      </b-alert>

      <template v-else>
        <div v-if="catalogue.length === 0" class="text-muted small py-3">
          No permissions available.
        </div>

        <div v-for="group in catalogue" :key="group.group" class="mb-3">
          <div class="d-flex justify-content-between align-items-center">
            <strong>{{ groupLabel(group.group) }}</strong>
            <b-button
              variant="link"
              size="sm"
              class="p-0"
              :disabled="systemRole"
              @click="toggleGroup(group)"
            >
              {{ allInGroupSelected(group) ? 'Clear' : 'Select all' }}
            </b-button>
          </div>
          <small
            v-if="groupHint(group.group)"
            class="text-muted d-block mb-1 ml-2"
          >
            {{ groupHint(group.group) }}
          </small>
          <b-form-checkbox
            v-for="perm in group.permissions"
            :key="perm.id"
            :checked="selectedIds.includes(perm.id)"
            :disabled="systemRole"
            class="py-1 ml-2"
            @change="togglePermission(perm.id, $event)"
          >
            {{ permissionLabel(perm) }}
            <template v-if="dependencyLabels(perm).length">
              <b-icon-info-circle
                :id="`perm-dep-${perm.id}`"
                class="ml-1 text-info"
                style="
                  cursor: pointer;
                  font-size: 0.85em;
                  vertical-align: middle;
                "
              />
              <b-popover
                :target="`perm-dep-${perm.id}`"
                triggers="hover focus"
                placement="right"
                custom-class="perm-dep-popover"
              >
                <template #title>Requires</template>
                <ul class="mb-0 pl-3">
                  <li v-for="label in dependencyLabels(perm)" :key="label">
                    {{ label }}
                  </li>
                </ul>
              </b-popover>
            </template>
            <small v-if="perm.description" class="d-block text-muted">
              {{ perm.description }}
            </small>
          </b-form-checkbox>
        </div>
      </template>

      <div class="d-flex justify-content-end mt-3" style="gap: 12px">
        <b-button variant="invisible" @click="close">Cancel</b-button>
        <b-button
          v-if="!systemRole"
          type="submit"
          variant="primary"
          :disabled="saving || !form.name"
        >
          <b-spinner v-if="saving" small class="mr-1" />
          {{ isEditing ? 'Save Changes' : 'Create Role' }}
        </b-button>
      </div>
    </b-form>
  </b-modal>
</template>

<script>
import ProjectAccessSelector from '@/components/forms/ProjectAccessSelector.vue'
import {
  permissionGroupLabel,
  permissionGroupHint,
  permissionItemLabel,
  filterPermissionCatalogue,
  mergeProjectGroups,
  stripDeprecatedPermissionIds,
} from '@/utils/permissionLabels'

/**
 * Permission prerequisites: selecting a key automatically selects every key it
 * depends on, and clearing a prerequisite clears the dependents that need it.
 * Mirrors the backend enforcement so a saved role is never in a broken state.
 *
 * Example: sharing a tradie exports their files, so `tradies.share` requires
 * `tradies.files.view`.
 */
const PERMISSION_DEPENDENCIES = {
  // Timesheets — view is the gate for all sub-actions.
  'timesheets.create': ['timesheets.view'],
  'timesheets.edit': ['timesheets.view'],
  'timesheets.delete': ['timesheets.view'],
  'timesheets.download': ['timesheets.view'],
  'timesheets.zones.create': ['timesheets.view'],
  'timesheets.zones.manage': ['timesheets.view'],
  // Both of these live inside the Timesheets area of the portal and are
  // reached from it, so granting either without view leaves a BA holding a
  // permission they have no screen to use it on.
  'timesheets.settings.manage': ['timesheets.view'],
  'timesheets.plant.manage': ['timesheets.view'],
  // Trades — view is the gate for all sub-actions.
  'trades.create': ['trades.view'],
  // Settings: users — view is the gate.
  'settings.users.manage': ['settings.users.view', 'settings.roles.apply'],
  // Settings: roles — view is the gate.
  'settings.roles.create': ['settings.roles.view'],
  'settings.roles.edit': ['settings.roles.view'],
  'settings.roles.delete': ['settings.roles.view'],
  'settings.roles.apply': ['settings.roles.view', 'settings.users.view'],
  // Requests — view is the gate for all sub-actions.
  'requests.create': ['requests.view'],
  'requests.message_log.view': ['requests.view'],
  'requests.delete': ['requests.view'],
  // Company files — view is the gate for all sub-actions.
  'files.upload': ['files.view'],
  'files.download': ['files.view'],
  'files.edit': ['files.view'],
  'files.share': ['files.view'],
  'files.delete': ['files.view'],
  'files.approve': ['files.view'],
  // Tradies — view is the gate for all sub-actions.
  'tradies.profile.view': ['tradies.view'],
  'tradies.create': ['tradies.view'],
  'tradies.share': ['tradies.view', 'tradies.files.view'],
  'tradies.remove': ['tradies.view'],
  'tradies.archive': ['tradies.view'],
  // Tradie files — require profile access since files live on the tradie profile.
  'tradies.files.view': ['tradies.view', 'tradies.profile.view'],
  'tradies.files.create': [
    'tradies.view',
    'tradies.profile.view',
    'tradies.files.view',
  ],
  'tradies.files.download': [
    'tradies.view',
    'tradies.profile.view',
    'tradies.files.view',
  ],
  'tradies.files.edit': [
    'tradies.view',
    'tradies.profile.view',
    'tradies.files.view',
  ],
  'tradies.files.delete': [
    'tradies.view',
    'tradies.profile.view',
    'tradies.files.view',
  ],
  // Notifications — requires visibility of who is being targeted.
  'notifications.send': ['tradies.view', 'projects.view'],
  // Projects — view is the gate for all sub-actions.
  'projects.create': ['projects.view'],
  'projects.edit': ['projects.view'],
  'projects.delete': ['projects.view'],
  'projects.archive': ['projects.view'],
  'projects.export': ['projects.view', 'projects.tradie.files.view'],
  'projects.share': ['projects.view', 'projects.tradie.files.view'],
  // Project tradies — all require view projects.
  'projects.tradies.add': ['projects.view', 'tradies.view'],
  'projects.tradies.remove': ['projects.view', 'tradies.view'],
  'projects.tradies.share': ['projects.view', 'projects.tradie.files.view'],
  'projects.tradie.trades.manage': ['projects.view'],
  // Project tradie files — view requires projects.view; all sub-actions require both.
  'projects.tradie.files.view': ['projects.view'],
  'projects.tradie.files.create': [
    'projects.view',
    'projects.tradie.files.view',
  ],
  'projects.tradie.files.edit': ['projects.view', 'projects.tradie.files.view'],
  'projects.tradie.files.delete': [
    'projects.view',
    'projects.tradie.files.view',
  ],
  'projects.tradie.files.download': [
    'projects.view',
    'projects.tradie.files.view',
  ],
  'projects.tradie.files.approve': [
    'projects.view',
    'projects.tradie.files.view',
  ],
  // Project files (the project's own documents) — same shape as tradie files.
  'projects.files.view': ['projects.view'],
  'projects.files.create': ['projects.view', 'projects.files.view'],
  'projects.files.edit': ['projects.view', 'projects.files.view'],
  'projects.files.delete': ['projects.view', 'projects.files.view'],
  'projects.files.download': ['projects.view', 'projects.files.view'],
}

export default {
  name: 'RoleEditorModal',

  components: { ProjectAccessSelector },

  props: {
    show: { type: Boolean, default: false },
    // null/undefined => create mode; an id => edit mode
    roleId: { type: [Number, String], default: null },
  },

  data() {
    return {
      catalogue: [],
      isLoading: false,
      saving: false,
      systemRole: false,
      rolePermissionKeys: [],
      form: {
        name: '',
        description: '',
        is_project_scoped: false,
      },
      selectedIds: [],
      // Project ids this role grants access to (when project-scoped).
      selectedProjectIds: [],
    }
  },

  computed: {
    isEditing() {
      return this.roleId != null
    },

    isAdminRole() {
      return this.rolePermissionKeys.includes('*')
    },

    allPermissionIds() {
      return this.catalogue.reduce(
        (ids, group) => ids.concat(group.permissions.map((p) => p.id)),
        [],
      )
    },

    allPermissionsSelected() {
      return (
        this.allPermissionIds.length > 0 &&
        this.allPermissionIds.every((id) => this.selectedIds.includes(id))
      )
    },

    /** Lookups between permission key and id, built from the catalogue. */
    permissionKeyMaps() {
      const idByKey = {}
      const keyById = {}
      const permByKey = {}
      this.catalogue.forEach((group) =>
        group.permissions.forEach((perm) => {
          idByKey[perm.key] = perm.id
          keyById[perm.id] = perm.key
          permByKey[perm.key] = perm
        }),
      )
      return { idByKey, keyById, permByKey }
    },
  },

  watch: {
    show(open) {
      if (open) this.initialise()
    },
  },

  methods: {
    async initialise() {
      this.isLoading = true
      this.resetForm()
      try {
        await this.loadCatalogue()
        if (this.isEditing) await this.loadRole()
      } finally {
        this.isLoading = false
      }
    },

    resetForm() {
      this.form = { name: '', description: '', is_project_scoped: false }
      this.selectedIds = []
      this.selectedProjectIds = []
      this.systemRole = false
      this.rolePermissionKeys = []
    },

    async loadCatalogue() {
      try {
        const { data } = await this.$api.get('company/permissions')
        this.catalogue = mergeProjectGroups(
          filterPermissionCatalogue((data && data.data) || []),
        )
      } catch (e) {
        this.catalogue = []
      }
    },

    async loadRole() {
      const { data } = await this.$api.get(`company/roles/${this.roleId}`)
      const role = (data && data.data) || data
      this.form.name = role.name || ''
      this.form.description = role.description || ''
      this.form.is_project_scoped = !!role.is_project_scoped
      this.systemRole = !!role.is_system
      this.rolePermissionKeys = Array.isArray(role.permission_keys)
        ? role.permission_keys
        : []
      this.selectedIds = this.isAdminRole
        ? []
        : stripDeprecatedPermissionIds(
            this.catalogue,
            this.resolveSelectedIds(role),
          )
      this.selectedProjectIds = this.resolveProjectIds(role)
    },

    /**
     * Seed the role's project ids, accepting either `project_ids: [1,2]` or a
     * `projects: [{ id: 1 }, ...]` relation.
     */
    resolveProjectIds(role) {
      const source = Array.isArray(role.project_ids)
        ? role.project_ids
        : Array.isArray(role.projects)
        ? role.projects
        : []
      return source
        .map((p) => (p && typeof p === 'object' ? p.id : p))
        .filter((id) => id != null)
    },

    /**
     * Seed selected ids from the role detail, accepting either `permission_ids`
     * (preferred) or `permission_keys` mapped back through the catalogue.
     */
    resolveSelectedIds(role) {
      if (Array.isArray(role.permission_ids)) return [...role.permission_ids]
      if (Array.isArray(role.permission_keys)) {
        const byKey = {}
        this.catalogue.forEach((g) =>
          g.permissions.forEach((p) => {
            byKey[p.key] = p.id
          }),
        )
        return role.permission_keys
          .map((k) => byKey[k])
          .filter((id) => id != null)
      }
      return []
    },

    groupLabel(group) {
      return permissionGroupLabel(group)
    },

    groupHint(group) {
      return permissionGroupHint(group)
    },

    permissionLabel(perm) {
      return permissionItemLabel(perm)
    },

    togglePermission(id, checked) {
      let next = this.selectedIds.filter((x) => x !== id)
      const key = this.permissionKeyMaps.keyById[id]

      if (checked) {
        next.push(id)
        // Auto-select all transitive prerequisites.
        this.allPrereqKeysOf(key).forEach((prereqKey) => {
          const prereqId = this.permissionKeyMaps.idByKey[prereqKey]
          if (prereqId != null && !next.includes(prereqId)) next.push(prereqId)
        })
      } else {
        // Clearing a prerequisite cascades to clear all transitive dependents.
        this.allDependentKeysOf(key).forEach((dependentKey) => {
          const dependentId = this.permissionKeyMaps.idByKey[dependentKey]
          if (dependentId != null) next = next.filter((x) => x !== dependentId)
        })
      }

      this.selectedIds = next
    },

    /** Keys that directly list `prereqKey` among their dependencies. */
    dependentKeysOf(prereqKey) {
      if (!prereqKey) return []
      return Object.keys(PERMISSION_DEPENDENCIES).filter((k) =>
        PERMISSION_DEPENDENCIES[k].includes(prereqKey),
      )
    },

    /** Recursively collects all transitive prerequisite keys. */
    allPrereqKeysOf(key, visited = new Set()) {
      if (visited.has(key)) return []
      visited.add(key)
      const direct = PERMISSION_DEPENDENCIES[key] || []
      const all = [...direct]
      direct.forEach((k) => all.push(...this.allPrereqKeysOf(k, visited)))
      return all
    },

    /** Recursively collects all transitive dependent keys. */
    allDependentKeysOf(prereqKey, visited = new Set()) {
      if (visited.has(prereqKey)) return []
      visited.add(prereqKey)
      const direct = this.dependentKeysOf(prereqKey)
      const all = [...direct]
      direct.forEach((k) => all.push(...this.allDependentKeysOf(k, visited)))
      return all
    },

    dependencyLabels(perm) {
      const prereqKeys = (perm && PERMISSION_DEPENDENCIES[perm.key]) || []
      return prereqKeys.map((key) => {
        const prereqPerm = this.permissionKeyMaps.permByKey[key]
        return prereqPerm ? this.permissionLabel(prereqPerm) : key
      })
    },

    allInGroupSelected(group) {
      return group.permissions.every((p) => this.selectedIds.includes(p.id))
    },

    toggleGroup(group) {
      const ids = group.permissions.map((p) => p.id)
      if (this.allInGroupSelected(group)) {
        this.selectedIds = this.selectedIds.filter((id) => !ids.includes(id))
      } else {
        const set = new Set(this.selectedIds)
        ids.forEach((id) => set.add(id))
        this.selectedIds = [...set]
      }
    },

    selectAllPermissions() {
      this.selectedIds = [...this.allPermissionIds]
    },

    clearAllPermissions() {
      this.selectedIds = []
    },

    async save() {
      if (this.systemRole || !this.form.name) return
      this.saving = true
      const payload = {
        name: this.form.name,
        description: this.form.description,
        is_project_scoped: this.form.is_project_scoped,
        permission_ids: this.selectedIds,
        // Projects only matter for a project-scoped role; send [] otherwise.
        project_ids: this.form.is_project_scoped ? this.selectedProjectIds : [],
      }
      try {
        if (this.isEditing) {
          await this.$api.put(`company/roles/${this.roleId}`, payload)
        } else {
          await this.$api.post('company/roles', payload)
        }
        this.$bvToast.toast(
          this.isEditing ? 'Role updated.' : 'Role created.',
          { title: 'Success', variant: 'success', autoHideDelay: 3000 },
        )
        this.$emit('saved')
        this.close()
      } catch (err) {
        const message =
          (err.response && err.response.data && err.response.data.message) ||
          'Failed to save role.'
        this.$bvToast.toast(message, {
          title: 'Oops.. Something went wrong!',
          variant: 'danger',
          autoHideDelay: 4000,
        })
      } finally {
        this.saving = false
      }
    },

    onVisibilityChange(visible) {
      if (!visible) this.$emit('update:show', false)
    },

    close() {
      this.$emit('update:show', false)
    },

    onHidden() {
      this.$emit('update:show', false)
    },
  },
}
</script>

<style scoped>
.scope-section {
  background-color: #f8f9fa;
}

/*
 * The app's global switch styles (main.scss) size the toggle inconsistently and
 * mis-place the "on" knob so it overflows the track. Rather than patch single
 * values, fully redefine this switch's geometry in scope so the knob stays inside
 * the track in both states. `>>>` pierces scoped styling to reach the
 * Bootstrap-generated label/pseudo-elements.
 *
 * Track is 44px wide; knob 18px. The label sits 2.25rem (36px) into the control,
 * and the pseudo-elements are positioned relative to it (global sets the label
 * position:relative), so `left: -2.25rem` places the track at the control's left
 * edge. Push the label text clear of the 44px track.
 */
.scope-switch >>> .custom-control-label {
  padding-left: 2rem;
  padding-top: 2px;
}

/* Track */
.scope-switch >>> .custom-control-label::before {
  width: 44px !important;
  height: 24px !important;
  border-radius: 12px !important;
  top: 2px;
  left: -2.25rem;
}

/* Knob */
.scope-switch >>> .custom-control-label::after {
  width: 18px !important;
  height: 18px !important;
  border-radius: 50% !important;
  top: 5px;
  left: calc(-2.25rem + 3px) !important;
  background-color: #fff !important;
  transform: none !important;
}

/* Knob slid right when on — stays 3px inside the track's right edge */
.scope-switch >>> .custom-control-input:checked ~ .custom-control-label::after {
  left: calc(-2.25rem + 23px) !important;
  transform: none !important;
}

/* Track colour when on */
.scope-switch
  >>> .custom-control-input:checked
  ~ .custom-control-label::before {
  background-color: #00d392 !important;
  border-color: #00d392 !important;
}
</style>

<style lang="scss">
.perm-dep-popover {
  .popover-header {
    background-color: #2c66d5;
    color: #fff;
    font-size: 0.875rem;
    font-weight: 600;
    padding: 6px 12px;
    border-bottom: none;
    border-radius: 5px 5px 0 0;
  }

  .popover-body {
    padding: 8px 12px;
    font-size: 0.875rem;

    ul {
      margin: 0;
      padding-left: 14px;

      li {
        padding: 1px 0;
        color: #333;
      }
    }
  }
}
</style>
